import { httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';

import {
  type FetchMerchantOrdersParams,
  MERCHANT_ORDERS_DEFAULT_PAGE_SIZE,
  type Merchant,
  type MerchantOrder,
  type MerchantOrdersResponse,
  MerchantOrdersResponseSchema,
  MerchantsResponseSchema,
} from './merchantOrdersTypes';

const ordersUrl = () => `${getServiceUrl()}/merchant-orders/orders`;
const merchantsUrl = () => `${getServiceUrl()}/merchant-orders/merchants`;

const buildQueryParams = (params: FetchMerchantOrdersParams = {}) => ({
  limit: params.limit ?? MERCHANT_ORDERS_DEFAULT_PAGE_SIZE,
  offset: params.offset ?? 0,
  sortBy: params.sortBy ?? 'orderDate',
  sortOrder: params.sortOrder ?? 'desc',
  ...(params.modifiedAfter !== undefined ? { modifiedAfter: params.modifiedAfter } : {}),
});

// Caps the paging loop so a server bug (e.g. a page that never shrinks) cannot spin forever.
const MAX_PAGES = 20;

const fetchOrdersPage = async (params?: FetchMerchantOrdersParams): Promise<MerchantOrdersResponse> => {
  const response = await httpClientInterface.send('GET', ordersUrl(), undefined, undefined, buildQueryParams(params));
  return MerchantOrdersResponseSchema.parse(response.data);
};

/** Orders can arrive without an id; those cannot be deduped, so they pass through untouched. */
const dedupeById = (orders: MerchantOrder[]): MerchantOrder[] => {
  const seen = new Set<string>();
  return orders.filter((order) => {
    if (!order.id) {
      return true;
    }
    if (seen.has(order.id)) {
      return false;
    }
    seen.add(order.id);
    return true;
  });
};

export const merchantOrdersApi = {
  async fetchOrders(params?: FetchMerchantOrdersParams): Promise<MerchantOrder[]> {
    return (await fetchOrdersPage(params)).resources;
  },

  /**
   * Every order, or nothing.
   *
   * The matcher only applies a match when exactly one order fits a transaction, so an order missing
   * from this list cannot contest the transaction it would have claimed — a partial fetch turns a
   * skip into a wrong match. Anything short of a clean full read therefore throws rather than
   * returning what arrived.
   */
  async fetchAllOrders(): Promise<MerchantOrder[]> {
    const collected: MerchantOrder[] = [];
    let totalSize: number | undefined;
    let offset = 0;
    let complete = false;

    for (let page = 0; page < MAX_PAGES; page += 1) {
      const { resources, metaData } = await fetchOrdersPage({ limit: MERCHANT_ORDERS_DEFAULT_PAGE_SIZE, offset });
      collected.push(...resources);
      // The latest page's count is the most current view of a collection the extension may be
      // writing to while we read it.
      totalSize = metaData?.totalSize ?? totalSize;

      const reachedTotal = totalSize !== undefined && collected.length >= totalSize;
      // With no totalSize to go on, fall back to reading a short page as the end.
      const shortPage = totalSize === undefined && resources.length < MERCHANT_ORDERS_DEFAULT_PAGE_SIZE;
      if (resources.length === 0 || reachedTotal || shortPage) {
        complete = true;
        break;
      }
      // Stride by what arrived, not by what we asked for: the server may cap a page below `limit`.
      offset += resources.length;
    }

    if (!complete) {
      throw new Error(`merchant orders paging hit the ${MAX_PAGES}-page cap`);
    }

    const orders = dedupeById(collected);
    if (totalSize !== undefined && orders.length !== totalSize) {
      throw new Error(`merchant orders incomplete: collected ${orders.length} of ${totalSize}`);
    }
    return orders;
  },

  async fetchMerchants(): Promise<Merchant[]> {
    const response = await httpClientInterface.send('GET', merchantsUrl());
    return MerchantsResponseSchema.parse(response.data);
  },

  async deleteOrder(id: string): Promise<void> {
    await httpClientInterface.send('DELETE', `${ordersUrl()}/${id}`);
  },
};
